import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import html2canvas from 'html2canvas';
import jsQR from 'jsqr';
import QRCode from 'qrcode';
import { MoneyMaskDirective } from '../../directives/money-mask.directive';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

export interface ContaPixOption {
  codigoInterno: string;
  conta: string;
  descricao: string;
  banco: string;
  codigoBanco: string;
  agencia: string;
  empresa: string;
  chavePix: string;
  imprimeOrcamento: string;
}

@Component({
  selector: 'app-gerar-cobranca-pix',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent, MoneyMaskDirective],
  templateUrl: './gerar-cobranca-pix.component.html',
  styleUrl: './gerar-cobranca-pix.component.css'
})
export class GerarCobrancaPixComponent implements AfterViewInit {
  @Input() contas: ContaPixOption[] = [];
  @Output() fechar = new EventEmitter<void>();
  @ViewChild('pixRetrato') pixRetrato?: ElementRef<HTMLElement>;
  @ViewChild('buscaContaInput') buscaContaInput?: ElementRef<HTMLInputElement>;
  @ViewChild('valorPixInput') valorPixInput?: ElementRef<HTMLInputElement>;

  buscaConta = '';
  contaSelecionada: ContaPixOption | null = null;
  valor: string | number | null = '';
  gerando = false;
  erro = '';
  resultado: any | null = null;
  valorGerado = 0;
  valorAberto = false;
  copiado = false;
  copiaColaReal = '';
  qrDecodificando = false;
  gerandoImagem = false;

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.buscaContaInput?.nativeElement.focus());
  }

  get contasFiltradas(): ContaPixOption[] {
    const termo = this.normalizar(this.buscaConta);
    if (!termo) return this.contas;
    if (termo.length < 3) {
      return this.contas.filter(conta => this.normalizar(conta.codigoInterno).includes(termo));
    }
    return this.contas.filter(conta => this.normalizar([
      conta.codigoInterno, conta.conta, conta.descricao, conta.banco,
      conta.codigoBanco, conta.agencia, conta.empresa
    ].join(' ')).includes(termo));
  }

  selecionarUnicaContaEncontrada(): void {
    const contas = this.contasFiltradas;
    if (contas.length === 1) this.selecionarConta(contas[0]);
  }

  avancarParaValor(event: Event): void {
    event.preventDefault();
    this.valorPixInput?.nativeElement.focus();
  }

  gerarComEnter(event: Event): void {
    event.preventDefault();
    if (!this.gerando) this.gerar();
  }

  selecionarConta(conta: ContaPixOption): void {
    this.contaSelecionada = conta;
    this.resultado = null;
    this.erro = '';
    this.copiado = false;
    this.copiaColaReal = '';
  }

  gerar(): void {
    this.erro = '';
    this.copiado = false;
    if (!this.contaSelecionada) {
      this.erro = 'Selecione uma conta para gerar a cobrança Pix.';
      return;
    }
    const numero = this.numero(this.valor);
    if (numero === null) {
      this.erro = 'Informe um valor válido.';
      return;
    }
    if (numero < 0) {
      this.erro = 'Informe um valor maior ou igual a zero.';
      return;
    }

    const valorAberto = numero === 0;
    const requisicao: Record<string, unknown> = {
      codigoInterno: this.contaSelecionada.codigoInterno,
      codctabcoint: this.contaSelecionada.codigoInterno,
      conta: this.contaSelecionada.conta
    };
    // Sem valor, o serviço gera um QR dinâmico sem quantia fixa para o pagador informar no banco.
    if (!valorAberto) requisicao['valor'] = numero.toFixed(2);

    this.gerando = true;
    this.gateway.gerarQrCodeImpressao(requisicao).subscribe({
      next: (retorno: any) => {
        this.gerando = false;
        if (retorno?.status && String(retorno.status) !== '1') {
          this.erro = retorno.statusMessage || 'O Sankhya não autorizou a geração da cobrança Pix.';
          return;
        }
        const bruto = retorno?.responseBody ?? retorno;
        let corpo = bruto;
        if (typeof bruto === 'string') {
          try { corpo = JSON.parse(bruto); } catch { corpo = {}; }
        }
        const contas = Array.isArray(corpo?.contas) ? corpo.contas : [];
        if (!corpo?.sucesso || !contas.length) {
          this.erro = corpo?.mensagem || corpo?.message || 'Não foi possível gerar a cobrança Pix.';
          return;
        }
        const encontrado = contas.find((conta: any) => this.corresponde(conta, this.contaSelecionada!));
        if (!encontrado && contas.length > 1) {
          this.erro = 'A conta selecionada não retornou uma cobrança Pix.';
          return;
        }
        this.resultado = encontrado || contas[0];
        this.valorGerado = numero;
        this.valorAberto = valorAberto;
        this.copiaColaReal = this.campoPix('copiaCola', 'copiaECola', 'copiaecola', 'codigoCopiaCola', 'payload');
      },
      error: (falha: any) => {
        this.gerando = false;
        this.resultado = null;
        this.copiaColaReal = '';
        this.copiado = false;
        this.erro = falha?.error?.mensagem || falha?.error?.message || falha?.message || 'Erro ao gerar a cobrança Pix.';
      }
    });
  }

  tentarNovamente(): void {
    if (this.gerando) return;
    this.erro = '';
    this.resultado = null;
    this.copiaColaReal = '';
    this.copiado = false;
    this.gerar();
  }

  valorFormatado(): string {
    return this.valorGerado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  campoPix(...nomes: string[]): string {
    for (const nome of nomes) {
      const valor = this.resultado?.[nome] ?? this.resultado?.[nome.toUpperCase()];
      if (valor !== undefined && valor !== null && String(valor).trim()) return String(valor);
    }
    return '';
  }

  iconeBanco(): string {
    const descricao = [
      this.campoPix('deposito', 'DEPOSITO'),
      this.campoPix('favorecido', 'FAVORECIDO'),
      this.contaSelecionada?.banco,
      this.contaSelecionada?.descricao
    ].filter(Boolean).join(' ').toLowerCase();
    const banco = ['bradesco', 'sicoob', 'santander']
      .find(nome => new RegExp(`\\b${nome}\\b`, 'i').test(descricao));
    return `assets/bancos/${banco || 'generico'}.svg`;
  }

  copiaECola(): string {
    return this.copiaColaReal || this.campoPix('copiaCola', 'copiaECola', 'copiaecola', 'codigoCopiaCola', 'payload');
  }

  async lerConteudoQr(event: Event): Promise<void> {
    if (this.copiaECola()) return;
    const imagem = event.target as HTMLImageElement;
    if (!imagem) return;
    this.qrDecodificando = true;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = imagem.naturalWidth;
      canvas.height = imagem.naturalHeight;
      const contexto = canvas.getContext('2d', { willReadFrequently: true });
      if (!contexto || !canvas.width || !canvas.height) throw new Error('QR sem dimensões');
      contexto.drawImage(imagem, 0, 0);
      const dados = contexto.getImageData(0, 0, canvas.width, canvas.height);
      this.copiaColaReal = jsQR(dados.data, dados.width, dados.height)?.data || '';
      if (!this.copiaColaReal) this.erro = 'Não foi possível ler o conteúdo do QR Code Pix.';
    } catch {
      this.copiaColaReal = '';
      this.erro = 'Não foi possível ler o conteúdo do QR Code Pix.';
    } finally {
      this.qrDecodificando = false;
    }
  }

  async copiarCopiaECola(): Promise<void> {
    const texto = this.copiaECola();
    if (!texto) {
      this.erro = 'O conteúdo do QR Code Pix ainda não foi lido.';
      return;
    }
    try {
      await navigator.clipboard.writeText(texto);
      this.copiado = true;
      setTimeout(() => this.copiado = false, 2200);
    } catch {
      this.erro = 'Não foi possível copiar o código Pix.';
    }
  }

  enviarPorWhatsapp(): void {
    const texto = this.copiaECola();
    if (!texto) {
      this.erro = 'O conteúdo do QR Code ainda não está disponível.';
      return;
    }
    const url = `https://web.whatsapp.com/send?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async baixarImagemPix(): Promise<void> {
    const elemento = this.pixRetrato?.nativeElement;
    if (!elemento) return;
    this.gerandoImagem = true;
    try {
      const escala = Math.max(8, Math.min(10, (window.devicePixelRatio || 1) * 4));
      const canvas = await html2canvas(elemento, {
        backgroundColor: '#ffffff',
        scale: escala,
        useCORS: true,
        imageTimeout: 15000,
        logging: false,
        onclone: (documento) => {
          const qr = documento.querySelector('.pix-qr-wrap img') as HTMLImageElement | null;
          if (qr) qr.style.visibility = 'hidden';
        }
      });
      this.desenharQrNoCanvasFinal(canvas, elemento, escala);
      const link = document.createElement('a');
      link.download = `gpl_qrcodepix_${this.nomeArquivoPix()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      this.erro = 'Não foi possível gerar a imagem Pix.';
    } finally {
      this.gerandoImagem = false;
    }
  }

  private desenharQrNoCanvasFinal(canvas: HTMLCanvasElement, elemento: HTMLElement, escala: number): void {
    const payload = this.copiaECola();
    const imagem = elemento.querySelector('.pix-qr-wrap img') as HTMLImageElement | null;
    if (!payload || !imagem) return;

    const elementoRect = elemento.getBoundingClientRect();
    const qrRect = imagem.getBoundingClientRect();
    const escalaX = canvas.width / elementoRect.width || escala;
    const escalaY = canvas.height / elementoRect.height || escala;
    const x = Math.round((qrRect.left - elementoRect.left) * escalaX);
    const y = Math.round((qrRect.top - elementoRect.top) * escalaY);
    const largura = Math.round(qrRect.width * escalaX);
    const altura = Math.round(qrRect.height * escalaY);
    const contexto = canvas.getContext('2d');
    if (!contexto || largura <= 0 || altura <= 0) return;

    // html2canvas deixa o contexto com a escala aplicada; as coordenadas abaixo
    // já estão em pixels finais, então resetamos para não aplicar a escala duas vezes.
    contexto.setTransform(1, 0, 0, 1, 0, 0);
    // Substitui o bitmap do html2canvas por módulos alinhados a pixels inteiros.
    contexto.fillStyle = '#ffffff';
    contexto.fillRect(x, y, largura, altura);
    const qr = QRCode.create(payload, { errorCorrectionLevel: 'M' });
    const quietZone = 4;
    const modulo = Math.floor(Math.min(largura, altura) / (qr.modules.size + quietZone * 2));
    if (modulo < 1) return;

    const tamanhoTotal = modulo * (qr.modules.size + quietZone * 2);
    const inicioX = x + Math.floor((largura - tamanhoTotal) / 2) + quietZone * modulo;
    const inicioY = y + Math.floor((altura - tamanhoTotal) / 2) + quietZone * modulo;
    contexto.fillStyle = '#000000';
    for (let linha = 0; linha < qr.modules.size; linha++) {
      for (let coluna = 0; coluna < qr.modules.size; coluna++) {
        if (qr.modules.get(linha, coluna)) {
          contexto.fillRect(inicioX + coluna * modulo, inicioY + linha * modulo, modulo, modulo);
        }
      }
    }
  }

  private nomeArquivoPix(): string {
    const agora = new Date();
    const dois = (valor: number) => String(valor).padStart(2, '0');
    return `${dois(agora.getDate())}${dois(agora.getMonth() + 1)}${agora.getFullYear()}${dois(agora.getHours())}${dois(agora.getMinutes())}${dois(agora.getSeconds())}`;
  }

  private corresponde(retorno: any, conta: ContaPixOption): boolean {
    const alvoCodigo = this.normalizarCodigo(conta.conta);
    const alvosTexto = [conta.conta, conta.descricao, conta.banco].map(valor => this.normalizar(valor)).filter(Boolean);
    return [retorno?.conta, retorno?.CONTA, retorno?.deposito, retorno?.DEPOSITO,
      retorno?.favorecido, retorno?.FAVORECIDO, retorno?.codigoInterno, retorno?.CODCTABCOINT].some(valor => {
        const codigo = this.normalizarCodigo(valor);
        const texto = this.normalizar(valor);
        const textoRelacionado = !!texto && alvosTexto.some(alvo => texto === alvo || texto.includes(alvo) || alvo.includes(texto));
        return (alvoCodigo && codigo === alvoCodigo) || textoRelacionado;
      });
  }

  private numero(valor: string | number | null): number | null {
    const texto = String(valor ?? '').trim().replace(/\s/g, '');
    if (!texto) return 0;
    const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto;
    const numero = Number(normalizado);
    return Number.isFinite(numero) ? numero : null;
  }

  private normalizar(valor: string): string {
    return String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');
  }

  private normalizarCodigo(valor: unknown): string {
    return String(valor ?? '').replace(/\D/g, '');
  }
}
